import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Building2, Kanban, ShoppingCart, Tag, UserCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../../context/AuthContext'
import { useProduct } from '../../context/ProductContext'
import { useCompany } from '../../context/CompanyContext'
import { useTarget } from '../../context/TargetContext'
import { useBusiness } from '../../context/BusinessContext'
import { api, apiHeaders } from '../../configs/api'
import Drawer from '../../components/common/Drawer'
import Label from '../../components/form/Label'
import Select from '../../components/form/Select'
import RichText from '../../components/form/RichText/Index'
import { BusinessType } from './Detail/BusinessDetail'
import { useContact } from '../../context/ContactContext'
import { colors } from '../../data/colors'

const BusinessForm = ({
    type,
    open,
    handleClose,
    handleConclude,
    pipeline,
    step,
    data
}: {
    type: 'create' | 'update'
    open: boolean
    handleClose: () => void
    handleConclude: (businessID: number, values: any) => void
    pipeline: any
    step: any | null
    data: BusinessType | null
}) => {
    const form = useForm({
        mode: 'onChange'
    })
    const formRef = React.useRef<HTMLFormElement>(null)
    const { user, unity, token } = useAuth()
    const { contacts } = useContact()
    const { products, loadProducts } = useProduct()
    const { companies } = useCompany()
    const { targets } = useTarget()
    const { business, currentTab } = useBusiness()
    const [steps, setSteps] = React.useState<any[]>([])
    const [filteredContacts, setFilteredContacts] = React.useState<any[]>([])

    // varre pipelines e converte label => name
    const pipelines = business.pipelines.map((pipeline: any) => ({
        id: pipeline.id,
        name: pipeline.label,
        value: pipeline.value,
        steps: pipeline.steps.map((step: any) => ({
            id: step.id,
            title: step.title,
            color: step.color
        }))
    }))

    const saveDatabase = async (values: any) => {
        try {
            const route = type === 'create' ? `${api}business/createBusiness` : `${api}business/updateBusiness`
            const response = await fetch(route, {
                method: 'POST',
                body: JSON.stringify(values)
            })
            const result = await response.json()
            return result.businessID
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const saveContext = (businessID: string, values: any) => {
        const pipelineID = values.pipeline[0].id
        const stepID = values.step[0].id
        if (!pipelineID || !stepID) return false

        try {
            const pipelineIndex = business.pipelines.findIndex((pipeline: any) => pipeline.id == pipelineID)
            if (pipelineIndex === -1) {
                return false
            }

            const steps = business.pipelines[pipelineIndex].steps
            const stepIndex = steps.findIndex((step: any) => step.id == stepID)
            if (stepIndex === -1) {
                return false
            }

            const step = steps[stepIndex]

            if (type === 'create') {
                // Criar novo business
                const newBusiness = {
                    id: businessID,
                    title: values.company[0].name,
                    subtitle: values.subtitle ?? '',
                    value: values.value ?? '',

                    contacts:
                        values.contacts?.map((contact: any) => ({
                            id: contact.id,
                            name: contact.name
                        })) ?? [],

                    products:
                        values.products?.map((product: any) => ({
                            id: product.id,
                            name: product.name
                        })) ?? [],

                    targets:
                        values.targets?.map((target: any) => ({
                            id: target.id,
                            label: target.name,
                            color: target.color
                        })) ?? [],

                    stepID: step.id,
                    dateNotify: null,
                    isExpired: false, //TODO ajustar
                    professionalID: null
                }

                // Inserir no step atual
                step.business.push(newBusiness)
            } else if (type === 'update') {
                // Atualizar business
                const businessIndex = step.business.findIndex((business: any) => business.id == data?.id)
                if (businessIndex === -1) {
                    return false
                }

                const business = step.business[businessIndex]

                business.title = values.company[0].name
                business.subtitle = values.subtitle ?? ''
                business.value = values.value ?? ''

                business.products =
                    values.products?.map((product: any) => ({
                        id: product.id,
                        name: product.name
                    })) ?? []

                business.targets = values.targets?.map((target: any) => ({
                    id: target.id,
                    label: target.name,
                    color: target.color
                }))

                business.stepID = step.id

                step.business[businessIndex] = business
            }

            return true
        } catch (error) {
            console.error(error)
            return false
        }
    }

    const handleSave = async (values: any) => {
        if (!unity || !user) return

        const formattedValues = {
            ...values,
            id: type === 'update' ? data?.id : null,
            step: [
                {
                    id: Number(values.step[0].id),
                    name: values.step[0].name
                }
            ],
            unityID: unity.unityID,
            userID: user.userID
        }

        try {
            const businessID = await saveDatabase(formattedValues)
            if (!businessID) {
                toast.error(`Não foi possível ${type === 'create' ? 'criar' : 'atualizar'} o negócio!`)
                return
            }

            const result = saveContext(businessID, formattedValues)
            if (result) {
                toast.success(type === 'create' ? 'Negócio criado com sucesso!' : 'Negócio atualizado com sucesso!')
                handleConclude(businessID, formattedValues)
            } else {
                toast.error(`Não foi possível ${type === 'create' ? 'criar' : 'atualizar'} o negócio!`)
            }
        } catch (error) {
            console.log(error)
        }
    }

    const handleNewCompany = async (name: string) => {
        if (!name || !unity) return

        try {
            const values = {
                name,
                email: null,
                phone: null,
                unityID: unity.unityID
            }
            const response = await fetch(`${api}company/create`, {
                method: 'POST',
                body: JSON.stringify(values)
            })
            const result = await response.json()
            if (!result.success) {
                return toast.error('Não foi possível criar a empresa!')
            }

            // Atualiza companies no contexto
            companies.push({
                id: result.company.id,
                name: result.company.name
            })

            form.setValue('company', [{ id: result.company.id, name: result.company.name }])
            toast.success('Empresa criada com sucesso!')
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const handleNewContact = async (name: string) => {
        const company = form.watch('company')
        if (!company) {
            return toast.error('Selecione uma empresa!')
        }
        const hasCompanySelected = company.length > 0 ? true : false
        const companyID = hasCompanySelected ? company[0].id : null
        if (!name || !unity || !hasCompanySelected || !companyID) {
            return toast.error('Preencha o nome da empresa!')
        }

        try {
            const values = {
                name,
                email: null,
                phone: null,
                companyID,
                unityID: unity.unityID
            }
            console.log(values)
            const response = await fetch(`${api}company/contact/create`, {
                method: 'POST',
                body: JSON.stringify(values)
            })
            const result = await response.json()
            if (!result.success) {
                return toast.error('Não foi possível criar o contato!')
            }

            // Atualiza contacts no contexto
            contacts.push({
                id: result.contact.id,
                name: result.contact.name,
                company: {
                    id: companyID,
                    name
                }
            })

            // contact +1 (push no react-hook-form)
            form.setValue('contacts', [
                ...form.watch('contacts'),
                {
                    id: result.contact.id,
                    name: result.contact.name,
                    company: {
                        id: companyID,
                        name
                    }
                }
            ])
            toast.success('Contato criado com sucesso!')
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const handleNewProduct = async (name: string) => {
        if (!name || !unity) {
            return toast.error('Preencha o nome do produto!')
        }

        try {
            const response = await fetch(`${api}product/create`, {
                method: 'POST',
                headers: apiHeaders(token),
                body: JSON.stringify({
                    name,
                    isDefault: false,
                    unityID: unity.unityID
                })
            })
            const result = await response.json()
            if (!result.success) {
                return toast.error(result.message || 'Não foi possível criar o produto!')
            }

            await loadProducts()

            // product +1 (push no react-hook-form)
            form.setValue('products', [
                ...(form.watch('products') ?? []),
                { id: result.product.id, name: result.product.name }
            ])
            toast.success('Produto criado com sucesso!')
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const handleNewTarget = async (name: string) => {
        if (!name || !unity) {
            return toast.error('Preencha o nome da etiqueta!')
        }

        try {
            const response = await fetch(`${api}target/create`, {
                method: 'POST',
                body: JSON.stringify({
                    name,
                    color: colors[Math.floor(Math.random() * colors.length)], //* cor aleatória do array colors
                    unityID: unity.unityID
                })
            })
            const result = await response.json()
            if (!result.success) {
                return toast.error('Não foi possível criar a etiqueta!')
            }

            // Atualiza targets no contexto
            targets.push({
                id: result.target.id,
                name: result.target.name,
                color: result.target.color
            })

            // target +1 (push no react-hook-form)
            form.setValue('targets', [
                ...(form.watch('targets') ?? []),
                { id: result.target.id, name: result.target.name, color: result.target.color }
            ])

            toast.success('Etiqueta criada com sucesso!')
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const handlePipelineChange = (value: any) => {
        form.setValue('pipeline', value) // Atualiza o react-hook-form
        form.setValue('step', []) // Reseta os steps ao trocar o pipeline

        // Filtra os steps do pipeline selecionado value[0].steps
        const filteredSteps = value?.[0]?.steps.map((step: any) => ({
            id: step.id,
            name: step.title,
            value: step.id
        }))
        setSteps(filteredSteps || [])
        form.setValue('step', [])
    }

    // Inicia pipeline com o pipeline atual
    const currentPipeline = pipelines.find((pipeline: any) => pipeline.value === currentTab)
    useEffect(() => {
        if (currentPipeline) {
            form.setValue('pipeline', [{ id: currentPipeline.id, name: currentPipeline.name }])
            // Atualiza as fases do pipeline atual
            const formattedSteps = currentPipeline.steps.map((step: any) => ({ id: step.id, name: step.title }))
            setSteps(formattedSteps)
        }
    }, [pipeline])

    // Inicia fase com o step atual
    useEffect(() => {
        if (step) {
            form.setValue('step', [{ id: step.id, name: step.title }])
        }
    }, [step])

    useEffect(() => {
        const selectedCompany = form.watch('company')?.[0]

        if (selectedCompany) {
            const filtered = contacts.filter(c => c.company?.id === selectedCompany.id)
            setFilteredContacts(filtered)

            const selectedContacts = form.getValues('contacts') || []
            const validContacts = selectedContacts.filter((c: any) => c.company?.id === selectedCompany.id)

            if (validContacts.length !== selectedContacts.length) {
                form.setValue('contacts', validContacts)
            }
        } else {
            setFilteredContacts([])
            form.setValue('contacts', [])
        }
    }, [form.watch('company'), contacts])

    useEffect(() => {
        if (type === 'update' && open && data) {
            form.setValue('company', [{ id: data.company.id, name: data.company.name }])

            // Selecionar contatos (baseado em company)
            const filtered = contacts.filter(c => c.company?.id === data.company.id)
            form.setValue('contacts', filtered)

            form.setValue('products', [
                ...data.products.map((row: any) => ({
                    id: row.id,
                    name: row.name
                }))
            ])
            form.setValue('targets', [
                ...data.targets.map((row: any) => ({
                    id: row.id,
                    name: row.label,
                    color: row.color
                }))
            ])
            form.setValue('step', [
                {
                    id: data.step.id,
                    name: data.step.title,
                    value: data.step.id
                }
            ])
            form.setValue('description', data.description)
        }

        // Se type === 'create', inicializa vazio
        if (type === 'create' && open) {
            form.setValue('company', [])
            form.setValue('contacts', [])
            form.setValue('products', [])
            form.setValue('targets', [])
            form.setValue('description', '')
        }
    }, [type, open, data])

    // if (!pipeline) return null

    return (
        <Drawer
            open={open}
            handleClose={handleClose}
            title={
                type == 'create' ? (
                    'Novo negócio'
                ) : (
                    <span>
                        Alterar negócio <span className='text-success-500'>#{data?.id}</span>
                    </span>
                )
            }
            handleSave={() => formRef.current?.requestSubmit()}
        >
            <form ref={formRef} onSubmit={form.handleSubmit(handleSave)}>
                <div className='space-y-3'>
                    <div>
                        <Label required>Nome da empresa</Label>
                        <Controller
                            name='company'
                            control={form.control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Select
                                    options={companies}
                                    onChange={field.onChange} // Atualiza o react-hook-form
                                    value={field.value || []} // Mantém o valor sincronizado
                                    allowNew
                                    key={'company'}
                                    icon={<Building2 className='w-5 h-5' />}
                                    handleNew={(inputValue: string) => handleNewCompany(inputValue)}
                                />
                            )}
                        />
                    </div>

                    <div>
                        <Label>Nome dos contatos</Label>
                        <Controller
                            name='contacts'
                            control={form.control}
                            render={({ field, fieldState }) => (
                                <Select
                                    options={filteredContacts}
                                    onChange={field.onChange} // Atualiza o react-hook-form
                                    value={field.value || []} // Mantém o valor sincronizado
                                    allowNew
                                    key={'contact'}
                                    multiple
                                    icon={<UserCheck className='w-5 h-5' />}
                                    handleNew={(inputValue: string) => handleNewContact(inputValue)}
                                    error={fieldState.error ? true : false}
                                />
                            )}
                        />
                    </div>

                    <div>
                        <Label required>Produtos</Label>
                        <Controller
                            name='products'
                            control={form.control}
                            rules={{ required: true }}
                            render={({ field, fieldState }) => (
                                <Select
                                    options={products}
                                    onChange={field.onChange} // Atualiza o react-hook-form
                                    value={field.value || []} // Mantém o valor sincronizado
                                    allowNew
                                    key={'product'}
                                    multiple
                                    icon={<ShoppingCart className='w-5 h-5' />}
                                    handleNew={(inputValue: string) => handleNewProduct(inputValue)}
                                    error={fieldState.error ? true : false}
                                />
                            )}
                        />
                    </div>

                    <div>
                        <Label>Etiquetas</Label>
                        <Controller
                            name='targets'
                            control={form.control}
                            render={({ field }) => (
                                <Select
                                    options={targets}
                                    onChange={field.onChange} // Atualiza o react-hook-form
                                    value={field.value || []} // Mantém o valor sincronizado
                                    allowNew
                                    key={'target'}
                                    icon={<Tag className='w-5 h-5' />}
                                    multiple
                                    handleNew={(inputValue: string) => handleNewTarget(inputValue)}
                                />
                            )}
                        />
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                        <div>
                            <Label required>Pipeline</Label>
                            <Controller
                                name='pipeline'
                                control={form.control}
                                rules={{ required: true }}
                                render={({ field, fieldState }) => (
                                    <Select
                                        options={pipelines}
                                        onChange={handlePipelineChange}
                                        value={field.value || []} // Mantém o valor sincronizado
                                        allowNew
                                        key={'pipeline'}
                                        icon={<Kanban className='w-5 h-5' />}
                                        error={fieldState.error ? true : false}
                                    />
                                )}
                            />
                        </div>

                        <div>
                            <Label required>Fase</Label>
                            <Controller
                                key={'step'}
                                name='step'
                                control={form.control}
                                rules={{ required: true }}
                                render={({ field, fieldState }) => (
                                    <Select
                                        key={'step'}
                                        options={steps}
                                        onChange={field.onChange} // Atualiza o react-hook-form
                                        value={field.value || []} // Mantém o valor sincronizado
                                        allowNew
                                        icon={<Kanban className='w-5 h-5' />}
                                        error={fieldState.error ? true : false}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div>
                        <Label>Descrição</Label>
                        <Controller
                            name='description'
                            control={form.control}
                            render={({ field }) => <RichText value={field.value} onChange={field.onChange} />}
                        />
                    </div>
                </div>
            </form>
        </Drawer>
    )
}

export default BusinessForm
